import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { RouteOffer } from '@/types/routeOffers';
import { formatCurrency, formatDate } from '@/lib/utils';
import { CheckCircle, Calendar, MapPin, Mail, MessageSquare } from 'lucide-react';

// Helper to convert dates from various formats
const toDate = (value: any) => {
  if (!value) return undefined;
  if (typeof value === 'string') return new Date(value);
  if (value.toDate) return value.toDate();
  if (value instanceof Date) return value;
  return value;
};

export default function OfferSuccess() {
  const { offerId } = useParams<{ offerId: string }>();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { user } = useAuth();
  const navigate = useNavigate();

  const [offer, setOffer] = useState<RouteOffer | null>(null);
  const [recipient, setRecipient] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  useEffect(() => {
    if (!offerId || !user) return;

    const loadOffer = async () => {
      try {
        const offerDoc = await getDoc(doc(db, 'routeOffers', offerId));

        if (offerDoc.exists()) {
          const data = offerDoc.data();
          const offerData = {
            id: offerDoc.id,
            ...data,
            expiresAt: toDate(data.expiresAt),
            createdAt: toDate(data.createdAt),
            updatedAt: toDate(data.updatedAt),
            recipients: data.recipients?.map((r: any) => ({
              ...r,
              viewedAt: toDate(r.viewedAt),
              respondedAt: toDate(r.respondedAt),
              paidAt: toDate(r.paidAt),
            })) || [],
          } as RouteOffer;

          setOffer(offerData);

          // Find the current recipient
          const currentRecipient = offerData.recipients.find(r => r.userId === user.uid);
          setRecipient(currentRecipient);
        }
      } catch (error) {
        console.error('Error loading offer:', error);
      } finally {
        setLoading(false);
      }
    };

    loadOffer();
  }, [offerId, user]);

  const handleSubmitFeedback = async () => {
    if (!feedback.trim() || !offerId || !user) return;

    try {
      setSubmittingFeedback(true);

      // Update the recipient's feedback in Firestore
      const offerRef = doc(db, 'routeOffers', offerId);
      const updatedRecipients = offer!.recipients.map(r => {
        if (r.userId === user.uid) {
          return { ...r, enrollmentFeedback: feedback.trim() };
        }
        return r;
      });

      await updateDoc(offerRef, {
        recipients: updatedRecipients
      });

      // Show success message
      alert('Thank you for your feedback! We\'ll be in touch soon.');
      setFeedback('');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again or contact us directly.');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-shuttlebee-yellow mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your confirmation...</p>
        </div>
      </div>
    );
  }

  if (!offer || !recipient) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-gray-600">Unable to load offer details.</p>
            <Button onClick={() => navigate('/dashboard')} className="mt-4">
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Success Message */}
        <Card className="mb-6 border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <CheckCircle className="w-12 h-12 text-green-600 flex-shrink-0" />
              <div>
                <h1 className="text-2xl font-bold text-green-900 mb-2">
                  Payment Successful!
                </h1>
                <p className="text-green-700">
                  Your enrollment has been confirmed. You'll receive a confirmation email shortly.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Route Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Route Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-lg text-shuttlebee-dark mb-2">
                {offer.routeName}
              </h3>
              <p className="text-sm text-gray-500">{offer.routeCategory}</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-sm text-gray-700">Pickup</p>
                  <p className="text-sm text-gray-600">{offer.pickupLocation}</p>
                  {offer.pickupTime && (
                    <p className="text-xs text-gray-500 mt-1">{offer.pickupTime}</p>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-sm text-gray-700">Dropoff</p>
                  <p className="text-sm text-gray-600">{offer.dropoffLocation}</p>
                  {offer.dropoffTime && (
                    <p className="text-xs text-gray-500 mt-1">{offer.dropoffTime}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-sm text-gray-700">Enrolled Weeks</p>
                <p className="text-sm text-gray-600">
                  {recipient.selectedWeeks?.length || 0} week{recipient.selectedWeeks?.length !== 1 ? 's' : ''} selected
                </p>
                {recipient.selectedWeeks && recipient.selectedWeeks.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Weeks: {recipient.selectedWeeks.sort((a: number, b: number) => a - b).join(', ')}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Summary */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Payment Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Rate per week per student</span>
                <span className="font-medium">{formatCurrency(recipient.weeklyRate || 0)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Number of students</span>
                <span className="font-medium">{recipient.studentCount || 1}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Number of weeks</span>
                <span className="font-medium">{recipient.selectedWeeks?.length || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium">{formatCurrency(recipient.subtotal || 0)}</span>
              </div>
              {recipient.referralDiscount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Referral Discount</span>
                  <span className="font-medium">-{formatCurrency(recipient.referralDiscount)}</span>
                </div>
              )}
              <div className="border-t pt-2 flex justify-between text-lg font-bold">
                <span>Total Paid</span>
                <span className="text-shuttlebee-dark">{formatCurrency(recipient.totalAmount || 0)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Next Steps */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>What's Next?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-shuttlebee-yellow flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-sm">Check Your Email</p>
                  <p className="text-sm text-gray-600">
                    We've sent a confirmation email with your route details and receipt.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-shuttlebee-yellow flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-sm">View Your Dashboard</p>
                  <p className="text-sm text-gray-600">
                    Access your enrolled routes and manage your account in your dashboard.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Minimum Enrollment Reached - Feedback Request */}
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-900">
              <MessageSquare className="w-5 h-5" />
              Route Update
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-blue-900 font-medium">
                Great news — this route has reached the minimum enrollment needed to move forward!
              </p>
              <p className="text-blue-800 text-sm">
                We may still add a stop to help fill the vehicle or accommodate additional families.
                If you have timing preferences or conflicts that could affect your participation,
                please let us know below. We'll follow up within two weeks if we're unable to accommodate specific requests.
              </p>
              <div>
                <label className="block text-sm font-medium text-blue-900 mb-2">
                  Your feedback (optional):
                </label>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Share any timing preferences, conflicts, or other concerns..."
                  rows={4}
                  className="w-full px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                />
              </div>
              <Button
                onClick={handleSubmitFeedback}
                disabled={submittingFeedback || !feedback.trim()}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {submittingFeedback ? 'Submitting...' : 'Submit Feedback'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-4">
          <Button onClick={() => navigate('/dashboard')} className="flex-1">
            Go to Dashboard
          </Button>
          <Button onClick={() => navigate('/')} variant="outline" className="flex-1">
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
}
